import React, { useEffect } from 'react';
import { Navigate, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { AUTH_ERROR_EVENT } from '../services/api';

interface ProtectedRouteProps {
  redirectPath?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ 
  redirectPath = '/sign-in' 
}) => {
  const { isAuthenticated, isLoading, signOut } = useAuth();
  const navigate = useNavigate();

  // Listen for authentication error events
  useEffect(() => {
    const handleAuthError = async (event: Event) => {
      // Sign out the user
      try {
        await signOut();
      } catch (error) {
        console.error('Error signing out after auth error:', error);
      }
      
      // Navigate to sign-in page with message
      navigate(redirectPath, { 
        state: { 
          message: 'Your session has expired. Please sign in again.',
          from: window.location.pathname
        } 
      });
    };

    // Add event listener
    window.addEventListener(AUTH_ERROR_EVENT, handleAuthError);

    // Clean up
    return () => {
      window.removeEventListener(AUTH_ERROR_EVENT, handleAuthError);
    };
  }, [navigate, redirectPath, signOut]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={redirectPath} replace state={{ from: window.location.pathname }} />;
  }

  // Render the protected content
  return <Outlet />;
};

export default ProtectedRoute;